import { FieldTypeEnum } from './custom-field.types';

export interface FieldTypeDisplayInfo {
  label: string;
  description: string;
}

/**
 * Get enum values from Zod enum
 */
export const FIELD_TYPE_VALUES = FieldTypeEnum.options;

/**
 * Standardized display names and descriptions for field types
 * Used across the application for consistent UI representation
 *
 * Organized by category for better UX in field type selectors
 */
export const FIELD_TYPE_DISPLAY_NAMES: Record<string, FieldTypeDisplayInfo> = {
  // === Text Fields ===
  'STRING': {
    label: 'Short Text',
    description: 'Single line text for names, titles, or short entries'
  },
  'TEXT': {
    label: 'Long Text',
    description: 'Multi-line text for descriptions, notes, or comments'
  },
  'RICH_TEXT': {
    label: 'Rich Text',
    description: 'Formatted text with bold, lists, links, and styling'
  },

  // === Number Fields ===
  'NUMBER': {
    label: 'Number',
    description: 'Numeric values for quantities, amounts, or calculations'
  },
  'PERCENTAGE': {
    label: 'Percentage',
    description: 'Progress bar display for completion rates or probability'
  },
  'CURRENCY': {
    label: 'Currency',
    description: 'Monetary values displayed with currency symbol and commas'
  },

  // === Date & Time Fields ===
  'DATE': {
    label: 'Date',
    description: 'Calendar date picker (without time)'
  },
  'DATETIME': {
    label: 'Date & Time',
    description: 'Date and time picker for precise scheduling'
  },
  'TIME': {
    label: 'Time',
    description: 'Time picker for hours and minutes'
  },

  // === Selection Fields ===
  'DROPDOWN': {
    label: 'Single Select',
    description: 'Choose one option from a predefined list'
  },
  'MULTISELECT': {
    label: 'Multi Select',
    description: 'Choose multiple options from a predefined list'
  },
  'BOOLEAN': {
    label: 'Checkbox',
    description: 'Yes/No or True/False toggle'
  },

  // === Reference Fields ===
  'REFERENCE': {
    label: 'Reference',
    description: 'Link to another record (entity, project, task, etc.)'
  },
  'MULTI_REFERENCE': {
    label: 'Multi-Reference',
    description: 'Link to multiple records'
  },
  'USER': {
    label: 'Person',
    description: 'Select a single user or team member'
  },
  'MULTI_USER': {
    label: 'People',
    description: 'Select multiple users or team members'
  },

  // === File Fields ===
  'FILE': {
    label: 'Single File',
    description: 'Upload a single file or image (logo, avatar)'
  },
  'ATTACHMENTS': {
    label: 'Attachments',
    description: 'Upload multiple files of any type'
  },

  // === Other Fields ===
  'LINK': {
    label: 'URL',
    description: 'Clickable web link or URL'
  },

  // === Calculated Fields ===
  'FORMULA': {
    label: 'Formula',
    description: 'Calculated automatically from other fields on the record'
  },
  'LOOKUP': {
    label: 'Lookup',
    description: 'Shows a field from a related record in another module'
  },
  'ROLLUP': {
    label: 'Rollup',
    description: 'Counts or aggregates related records (sum, average, min, max)'
  },

  // === Legacy (hidden from UI, kept for backward compatibility) ===
  'MODULE_SEARCH': {
    label: 'Reference',
    description: 'Link to another record (legacy)'
  },
};

/**
 * Helper function to get the display name for a field type
 */
export function getFieldTypeDisplayName(fieldType: string): string {
  return FIELD_TYPE_DISPLAY_NAMES[fieldType]?.label || fieldType;
}

/**
 * Helper function to get the description for a field type
 */
export function getFieldTypeDescription(fieldType: string): string {
  return FIELD_TYPE_DISPLAY_NAMES[fieldType]?.description || '';
}

/**
 * Export an array of field types with their display properties
 * Useful for UI components that need to list all available field types
 */
export const FIELD_TYPE_OPTIONS = Object.entries(FIELD_TYPE_DISPLAY_NAMES).map(([key, value]) => ({
  value: key,
  label: value.label,
  description: value.description
}));

/**
 * Legacy field types that should be hidden from UI but still supported for existing data
 */
export const LEGACY_FIELD_TYPES = ['MODULE_SEARCH'] as const;

/**
 * Field types available for creating new custom fields
 * Excludes legacy types that are only kept for backward compatibility
 */
export const CUSTOM_FIELD_TYPE_OPTIONS = FIELD_TYPE_OPTIONS.filter(
  option => !LEGACY_FIELD_TYPES.includes(option.value as typeof LEGACY_FIELD_TYPES[number])
);

/**
 * Field types organized by category for better UI presentation
 */
export const FIELD_TYPE_CATEGORIES = {
  text: {
    label: 'Text',
    types: ['STRING', 'TEXT', 'RICH_TEXT']
  },
  number: {
    label: 'Number',
    types: ['NUMBER', 'PERCENTAGE', 'CURRENCY']
  },
  dateTime: {
    label: 'Date & Time',
    types: ['DATE', 'DATETIME', 'TIME']
  },
  selection: {
    label: 'Selection',
    types: ['DROPDOWN', 'MULTISELECT', 'BOOLEAN']
  },
  reference: {
    label: 'Reference',
    types: ['REFERENCE', 'MULTI_REFERENCE', 'USER', 'MULTI_USER']
  },
  files: {
    label: 'Files',
    types: ['FILE', 'ATTACHMENTS']
  },
  other: {
    label: 'Other',
    types: ['LINK']
  },
  calculated: {
    label: 'Calculated',
    types: ['FORMULA', 'LOOKUP', 'ROLLUP']
  }
} as const;